import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import api, { logout, storeSession } from '../../utils/authUtils';

// The route guards (P13), wired in since the switch (P15). The three roles are the ones
// authority.AuthorityService computes: ADMIN (accounts.role), MEMBER (every account) and SPEAKER (a
// person who was asked to a program assignment, N4).
//
// Contract with the backend (P12): GET /api/me answers 200 with at least
//   { "roles": ["MEMBER", "SPEAKER"] }     role names without the ROLE_ prefix (AuthorityService.rolesOf)
// and 401/403 for a missing, expired or pre-release token.
//
// Only a refused login means "logged out", and the two kinds are told apart (utils/authUtils):
//   - a session that existed and ended (the tokens expired, or the refresh was refused): the response
//     interceptor answers 'Session expired. Please login again.' and the guard goes to the login page
//     with ?session_expired=true, which shows that message;
//   - a visitor who never logged in (no tokens at all): /api/me answers a plain 401 and the guard goes
//     to the login page without any message, as before the switch.
// Both kinds clear the cached role as well, so the navbar stops showing a logged in person, and carry
// the page asked for in ?redirect= so the login returns there (the login pages honour only a path
// of this site, utils/redirectUtils).
// Every other failure - the request never reached the server (network error, timeout, aborted), or
// the server answered 403/5xx - is not the person's fault and must not look like a logged out
// browser: it shows the error block with a Retry instead.
//
// The guard never trusts the roles cached in localStorage by an earlier login: that cache is one
// refresh behind (POST /api/refresh-token carries no roles) and a browser that logged in before the
// switch still holds the retired ATTENDEE / SPEAKER names, so it would let the wrong people in and
// lock the right ones out. On every mount the guard asks /api/me, decides on that answer, and then
// replaces the cache with it.

const RoleRoute = ({ role, loginPath = '/login', deniedPath = '/', children }) => {
  const [status, setStatus] = useState('checking');
  // Retry re-runs the check effect by bumping this, so a failed request can be asked again without a
  // full page reload.
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = `redirect=${encodeURIComponent(location.pathname + location.search)}`;

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get('/api/me');
        const roles = Array.isArray(data && data.roles) ? data.roles : [];
        storeSession({ roles });
        if (!cancelled) setStatus(roles.includes(role) ? 'allowed' : 'denied');
      } catch (error) {
        if (cancelled) return;
        // Only a refused login is "logged out" (see the header): the interceptor's expiry message, or a
        // 401. Anything else is a failure to ask, not an answer of "no".
        if (error && error.message === 'Session expired. Please login again.') {
          navigate(`${loginPath}?session_expired=true&${redirect}`, { replace: true });
        } else if (error && error.response && error.response.status === 401) {
          logout();
          setStatus('anonymous');
        } else {
          setStatus('error');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, [role, loginPath, navigate, redirect, attempt]);

  if (status === 'checking') return <div>Loading...</div>;
  if (status === 'anonymous') return <Navigate to={`${loginPath}?${redirect}`} replace />;
  if (status === 'denied') return <Navigate to={deniedPath} replace />;
  if (status === 'error') {
    return (
      <div>
        <p>Could not check your access. Please try again.</p>
        <button
          type="button"
          onClick={() => { setStatus('checking'); setAttempt((n) => n + 1); }}
        >
          Retry
        </button>
        <p><Link to={loginPath}>Log in</Link></p>
      </div>
    );
  }
  return children;
};

export const MemberRoute = (props) => <RoleRoute role="MEMBER" {...props} />;

export const SpeakerRoute = (props) => <RoleRoute role="SPEAKER" {...props} />;

// The admin area has its own login page, so it keeps its own redirect; a logged-in non-admin lands
// on the homepage instead of the login form.
export const AdminRoute = (props) => <RoleRoute role="ADMIN" loginPath="/admin/login" {...props} />;

export default RoleRoute;
